import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import { API_URL } from '../constants/urls';

interface OrganizationAccess {
  organization_id: string;
  plan_type: string;
  billing_type: string;
  subscription_status?: string;
  current_period_end?: string;
  is_trial_active: boolean;
  trial_end_date?: string;
  default_trial_days: number;
  current_users: number;
  current_editors: number;
  current_propositions: number;
  current_segments: number;
  max_users?: number;
  max_editors?: number;
  max_propositions?: number;
  max_segments?: number;
  has_genai_access: boolean;
  has_integrations_access: boolean;
  has_api_access: boolean;
  has_advanced_analytics: boolean;
  has_priority_support: boolean;
}

export const useOrganizationAccess = () => {
  const { token } = useAuth();
  const { selectedOrg } = useOrganization();
  const [access, setAccess] = useState<OrganizationAccess | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedOrg || !token) {
      setLoading(false);
      return;
    }

    const fetchAccess = async () => {
      try {
        const response = await fetch(
          `${API_URL}/billing/limits?organization_id=${selectedOrg.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error('Failed to fetch organization access');
        }

        const data = await response.json();
        setAccess(data);
        setError(null);
      } catch (err) {
        // console.error removed
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to fetch access information',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchAccess();
  }, [selectedOrg, token]);

  return {
    access,
    loading,
    error,
    hasGenAIAccess: access?.has_genai_access || false,
    isOnTrial: access?.is_trial_active || false,
    planType: access?.plan_type || 'free',
  };
};
